import { requisitarJson } from '../../../shared/api/http';

export interface Participante {
    id: string;
    turma_id: string;
    grupo_id: string | null;
    grupo_nome: string | null;
    matricula: string;
    nome: string | null;
    papel: 'lider' | 'participante';
    situacao: 'ativo' | 'inativo';
    /** Bloqueado por tentativas de login; a situação continua Ativo (`DP-PT-004`). */
    bloqueado: boolean;
    conexao: 'online' | 'instavel' | 'offline';
    tablet: string | null;
    ultimo_acesso: string | null;
}

export interface PaginaDeParticipantes {
    /** Total do conjunto filtrado, não da página (`RN-PT-025`). */
    total: number;
    pagina: number;
    por_pagina: number;
    paginas: number;
    itens: Participante[];
}

export interface TurmaDaOpcao {
    id: string;
    nome: string | null;
    grupos: { id: string; nome: string | null }[];
}

export interface Criterios {
    busca?: string;
    turma_id?: string;
    /** `sem_grupo` filtra quem não tem grupo. */
    grupo_id?: string;
    papel?: 'lider' | 'participante';
    conexao?: 'online' | 'instavel' | 'offline';
    situacao?: 'ativo' | 'inativo';
    pagina: number;
}

export function listarParticipantes(criterios: Criterios): Promise<PaginaDeParticipantes> {
    // Critério vazio não vai na URL: o servidor trata ausência como "todos".
    const parametros = new URLSearchParams();
    for (const [chave, valor] of Object.entries(criterios)) {
        if (valor !== undefined && valor !== '') parametros.set(chave, String(valor));
    }
    return requisitarJson(`/api/v1/participantes?${parametros}`);
}

export function opcoesDeFiltro(): Promise<{ turmas: TurmaDaOpcao[] }> {
    return requisitarJson('/api/v1/participantes/filtros');
}

/**
 * Inativar e reativar usam a escrita que já existe na aba Jogadores, com o valor anterior: se
 * outro operador mudou antes, o servidor recusa em vez de sobrescrever.
 */
export function alterarSituacao(pedido: {
    participante: Participante;
    situacao: Participante['situacao'];
    csrfToken: string;
}): Promise<unknown> {
    const { participante, situacao, csrfToken } = pedido;
    return requisitarJson(`/api/v1/jogadores/${encodeURIComponent(participante.id)}`, {
        method: 'PATCH',
        csrfToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            turma_id: participante.turma_id,
            campo: 'situacao',
            valor: situacao,
            // O servidor compara com a situação que ele conhece, e para ele o bloqueado é 'bloqueado'
            // (`services/jogadores/campos.py`), mesmo que a lista o mostre como Ativo (`DP-PT-004`).
            valor_anterior: participante.bloqueado ? 'bloqueado' : participante.situacao,
            novo_lider: null,
        }),
    });
}

export interface ParticipanteCriado {
    id: string;
    turma_id: string;
    grupo_id: string | null;
    matricula: string;
    nome: string;
    /** Aparece só aqui, uma vez (`DP-PT-011`). */
    codigo: string;
}

/** Cadastro pela tela (`PT6`): a matrícula vai já normalizada; sem grupo vai como `null`. */
export function cadastrarParticipante(pedido: {
    turmaId: string;
    matricula: string;
    nome: string;
    grupoId: string | null;
    csrfToken: string;
}): Promise<ParticipanteCriado> {
    return requisitarJson('/api/v1/participantes', {
        method: 'POST',
        csrfToken: pedido.csrfToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ turma_id: pedido.turmaId, matricula: pedido.matricula, nome: pedido.nome, grupo_id: pedido.grupoId }),
    });
}

/** Editar dados muda só o nome (`RN-PT-041`, `DP-PT-027`): o corpo não leva mais nada. */
export function editarNome(pedido: { participante: Participante; nome: string; csrfToken: string }): Promise<unknown> {
    const { participante, nome, csrfToken } = pedido;
    return requisitarJson(
        `/api/v1/cadastro/turmas/${encodeURIComponent(participante.turma_id)}/participantes/${encodeURIComponent(participante.id)}`,
        { method: 'PATCH', csrfToken, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ nome }) },
    );
}

/** Novo código de acesso (`DP-PT-028`): o anterior deixa de valer e a sessão do tablet cai. */
export function gerarCodigo(pedido: { participante: Participante; csrfToken: string }): Promise<{ codigo: string }> {
    const { participante, csrfToken } = pedido;
    return requisitarJson(
        `/api/v1/cadastro/turmas/${encodeURIComponent(participante.turma_id)}/participantes/${encodeURIComponent(participante.id)}/codigo`,
        { method: 'POST', csrfToken },
    );
}

/** Uma pessoa da planilha. O `codigo` só vem ao aplicar, e só nesta resposta. */
export interface ParticipanteImportado {
    linha: number;
    matricula: string;
    nome: string;
    grupo_id: string | null;
    lider: boolean;
    codigo?: string | null;
}

/** O que a importação fez (`aplicado`) ou faria (conferir). */
export interface ResultadoDaImportacao {
    aplicado: boolean;
    criados: ParticipanteImportado[];
    /** Matrículas que já existiam nesta turma: ignoradas e não alteradas. */
    ja_existiam: string[];
    grupos_criados: string[];
    /** Criados sem grupo: só entram no jogo depois de alocados. */
    sem_grupo: string[];
    /** Líder definido por esta importação, por grupo. */
    lideres: Record<string, string>;
    avisos: string[];
}

const TIPO_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Importação por planilha: o `.xlsx` vai como corpo da requisição. Sem `aplicar` só confere (nada é
 * gravado nem sorteado); com `aplicar` grava tudo ou nada e devolve o código de cada pessoa criada.
 */
export function importarPlanilha(pedido: {
    turmaId: string;
    arquivo: File;
    aplicar: boolean;
    csrfToken: string;
}): Promise<ResultadoDaImportacao> {
    const consulta = new URLSearchParams({ turma_id: pedido.turmaId, aplicar: String(pedido.aplicar) });
    return requisitarJson(`/api/v1/participantes/importacao?${consulta}`, {
        method: 'POST',
        csrfToken: pedido.csrfToken,
        headers: { 'Content-Type': TIPO_XLSX },
        body: pedido.arquivo,
    });
}
